import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import MeetingScheduler from '../../team-workspace/components/MeetingScheduler';
import Icon from '../../../components/AppIcon';

const MeetingsTab = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [allUsers, setAllUsers] = useState([]);

  useEffect(() => {
    fetchAllUsers();
  }, [user?.id]);

  // NEW: Fetch all users from user_profiles for PCF Admin
  const fetchAllUsers = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch all users with their team information
      const { data: users, error: usersError } = await supabase
        ?.from('user_profiles')
        ?.select(`
          id,
          full_name,
          email,
          role,
          team_id,
          teams (
            team_number,
            team_name
          )
        `)
        ?.order('full_name');

      if (usersError) throw usersError;

      // Format users with team number or role
      const formattedUsers = (users || [])?.map(user => {
        // For students with team number, show team number
        if (user?.role === 'student' && user?.teams?.team_number) {
          return {
            id: user?.id,
            name: user?.full_name,
            email: user?.email,
            role: user?.role,
            teamNumber: user?.teams?.team_number,
            displayLabel: `${user?.full_name} (Team ${user?.teams?.team_number})`
          };
        }
        // For non-students or students without team, show role
        else {
          const roleDisplay = user?.role === 'pcf_admin' ? 'PCF Admin' :
                             user?.role === 'city_official' ? 'City Official' :
                             user?.role === 'business' ? 'Business Partner' :
                             user?.role === 'student'? 'Student' : user?.role ||'User';
          
          return {
            id: user?.id,
            name: user?.full_name,
            email: user?.email,
            role: user?.role,
            teamNumber: null,
            displayLabel: `${user?.full_name} (${roleDisplay})`
          };
        }
      });

      setAllUsers(formattedUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  if (loading && allUsers?.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 border border-error text-error rounded-lg p-4">
        <div className="flex items-center space-x-2">
          <Icon name="AlertTriangle" size={20} />
          <p className="font-medium">Error loading users</p>
        </div>
        <p className="text-sm mt-1">{error}</p>
      </div>
    );
  }

  if (allUsers?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="Calendar" size={48} className="mx-auto mb-4 text-muted-foreground" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Users Found</h3>
        <p className="text-muted-foreground">
          There are no users available in the system yet.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Meeting Scheduler with all users - No team selection needed */}
      <MeetingScheduler 
        teamId={null} // PCF Admin doesn't need a specific team
        allUsers={allUsers} // Pass all users instead of team members
        isPCFAdmin={true} // Flag to indicate this is PCF Admin view
      />
    </div>
  );
};

export default MeetingsTab;